import React, { useState, FunctionComponent, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Presentation } from './presentation';
import type { MessageTemplateModalState } from './types';
import { fetchMessageTemplates, saveMessageTemplate, updateMessageTemplate, deleteMessageTemplate } from '../../../services/messageTemplates';
import { getCsrfToken } from '../../../libs/csrfToken';

type Props = {
  targetArea?: string;
  targetName?: string;
  editingStatus: 'save' | 'update' | 'send'
  handleModalOpen: () => void;
  handleModalClose: () => void;
  onApplyTemplate?: (value: string) => void;
};

export const Component: FunctionComponent<Props> = ({
  targetArea = '',
  targetName = '',
  editingStatus,
  handleModalClose,
  onApplyTemplate,
}) => {
  const [state, setState] = useState<MessageTemplateModalState>({
    editingStatus,
    isFocus: false,
    templates: [],
    dustboxStyle: {},
    hoverStyle: {},
    targetSpan: '',
    targetId: '',
    title: '',
    body: '',
    id: ''
  });

  const fetchTemplates = useCallback(async() => {
    try {
      const response = await fetchMessageTemplates();
      setState(prev => ({ ...prev, templates: response.templates }));
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    setState(prev => ({ ...prev, editingStatus: editingStatus === 'save' ? 'save' : 'send' }));
  }, [editingStatus, fetchTemplates]);

  useEffect(() => {
    fetchTemplates();
  }, [state.title, state.body]);

  const onChangedTitle = (value: string) => setState(prev => ({ ...prev, title: value, editingStatus: prev.targetId === '' ? 'save' : 'update' }));
  const onChangedBody = (value: string) => {
    if (value.length > 2000) {
      alert('入力できるのは2000字までです。');
    }
    setState(prev => ({ ...prev, body: value, editingStatus: prev.targetId === '' ? 'save' : 'update' }));
  };

  const handleFetchedTemplate = (event: React.MouseEvent<HTMLSpanElement>) => {
    const el = event.currentTarget;
    const title = el.textContent || '';
    const body = el.getAttribute('data-body') || '';
    const id = el.getAttribute('data-id') || '';

    const style = { backgroundColor: '#EDEDED', color: '#19190F', fontWeight: 'bold' };
    const dustStyle = { display: 'block' };
    setState(prev => ({
      ...prev,
      title,
      body,
      targetId: id,
      targetSpan: title,
      hoverStyle: style,
      dustboxStyle: dustStyle,
      editingStatus: editingStatus === 'send' ? 'send' : 'update',
      isFocus: false,
      id,
    }));
  };

  const handleFormReset = () => {
    setState(prev => ({
      ...prev,
      editingStatus: 'save',
      title: '',
      body: '',
      targetSpan: '',
      targetId: '',
      isFocus: true
    }));
  };

  const handlePasteTemplate = () => {
    const content = targetName !== '' && state.body.includes('{{name}}')
      ? state.body.replace(/{{name}}/g, targetName)
      : state.body;
    $(targetArea).val(content);
    onApplyTemplate?.(content);
    handleModalClose();
  };

  const handleSaveTemplate = async () => {
    if (state.templates.some(t => t.title === state.title)) {
      alert('同じタイトルでは保存できません。');
      return;
    }
    if (!state.title || !state.body || state.body.length > 2000) {
      alert(state.title === '' ? 'タイトルを入力してください。' : state.body === '' ? '本文を入力してください。' : '2000字以上入力することはできません。');
      return;
    }
    const body = JSON.stringify({
      title: state.title,
      body: state.body,
    });
    try {
      await saveMessageTemplate(body, getCsrfToken());
      setState(prev => ({
        ...prev,
        editingStatus: 'save',
        title: '',
        body: '',
        targetSpan: '',
        isFocus: false
      }));
    } catch (error) {
      alert(error);
    }
  };

  const handleUpdateTemplate = async () => {
    const body = JSON.stringify({
      title: state.title,
      body: state.body,
    });
    try {
      await updateMessageTemplate(state.id, body, getCsrfToken());
      setState(prev => ({
        ...prev,
        editingStatus: 'update',
        title: '',
        body: '',
        targetSpan: '',
        isFocus: false
      }));
    } catch (error) {
      alert(error);
    }
  };

  const handleDeletedTemplate = async (event: React.MouseEvent<HTMLSpanElement>) => {
    if (!confirm('本当に削除してよろしいですか？')) return;
    const id = Number(event.currentTarget.getAttribute('data-id'));
    try {
      await deleteMessageTemplate(id, getCsrfToken());
      setState(prev => ({
        ...prev,
        editingStatus: 'send',
        title: '',
        body: '',
        targetSpan: '',
        isFocus: false
      }));
    } catch (error) {
      alert('テンプレートを削除できませんでした。' + error);
    }
  };

  const modal = (
    <Presentation
      state={state}
      targetName={targetName}
      targetArea={targetArea}
      onChangedTitle={onChangedTitle}
      onChangedBody={onChangedBody}
      handleModalClose={handleModalClose}
      handleFormReset={handleFormReset}
      handleFetchedTemplate={handleFetchedTemplate}
      handleDeletedTemplate={handleDeletedTemplate}
      handlePasteTemplate={handlePasteTemplate}
      handleSaveTemplate={handleSaveTemplate}
      handleUpdateTemplate={handleUpdateTemplate}
    />
  );

  if (typeof document === 'undefined') return modal;

  return createPortal(modal, document.body);
};
